import { useEffect } from 'react';
import { LinearProgress } from '@mui/material';
import { useStoreState } from 'easy-peasy';
import toast from 'react-hot-toast';

const UploadBlockingOverlay = () => {
  const { isUploading, progress, fileName } = useStoreState((state) => state.upload);

  useEffect(() => {
    if (!isUploading) return;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    const handleRefreshShortcut = (event) => {
      const isRefreshKey =
        event.key === 'F5' ||
        event.code === 'F5' ||
        (typeof event.keyCode === 'number' && event.keyCode === 116);
      const normalizedKey = typeof event.key === 'string' ? event.key.toLowerCase() : '';
      const isReloadCombo =
        (event.ctrlKey || event.metaKey) && (normalizedKey === 'r' || event.code === 'KeyR');

      if (!isRefreshKey && !isReloadCombo) return;

      event.preventDefault();
      event.stopPropagation();
      toast('Upload is in progress. Please wait until it finishes.');
    };

    const handlePopState = () => {
      toast('Upload is in progress. Navigation is temporarily locked.');
      window.history.pushState(null, '', window.location.href);
    };

    window.history.pushState(null, '', window.location.href);
    window.onbeforeunload = handleBeforeUnload;
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('keydown', handleRefreshShortcut, true);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.onbeforeunload = null;
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('keydown', handleRefreshShortcut, true);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isUploading]);

  if (!isUploading) return null;

  return (
    <div className="upload-blocker-root" role="dialog" aria-modal="true" aria-live="polite">
      <div className="upload-blocker-card">
        <h3>Uploading Presentation</h3>
        <p>Please keep this tab open. We are uploading your PPT file.</p>
        {fileName && <p className="upload-filename">{fileName}</p>}
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: '999px',
            backgroundColor: 'var(--theme-color-secondary)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: 'var(--btn-purple-color)'
            }
          }}
        />
        <div className="upload-progress-label">{progress}% complete</div>
      </div>

      <style jsx>{`
        .upload-blocker-root {
          position: fixed;
          inset: 0;
          z-index: 2500;
          display: flex;
          align-items: center;
          justify-content: center;
          background:
            radial-gradient(circle at 24% 22%, rgba(221, 247, 129, 0.2), transparent 50%),
            radial-gradient(circle at 76% 74%, rgba(48, 26, 255, 0.1), transparent 52%),
            rgba(28, 28, 28, 0.52);
          backdrop-filter: blur(2px);
          padding: 20px;
          pointer-events: all;
        }

        .upload-blocker-card {
          width: min(560px, 100%);
          background: var(--white);
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
          border: 1px solid var(--theme-color-secondary);
        }

        .upload-blocker-card h3 {
          margin: 0;
          color: var(--gray-darkest);
          font-size: 22px;
          font-weight: 700;
        }

        .upload-blocker-card p {
          margin: 10px 0;
          color: var(--gray-light);
          font-size: 14px;
        }

        .upload-filename {
          font-weight: 600;
          color: var(--gray-darkest);
          word-break: break-word;
        }

        .upload-progress-label {
          margin-top: 8px;
          color: var(--btn-purple-color);
          font-weight: 600;
          font-size: 13px;
          text-align: right;
        }
      `}</style>
    </div>
  );
};

export default UploadBlockingOverlay;
